import {prefetchResource} from "api/resourceCache";
import {CONTACTS_CACHE_KEY, GROUPS_CACHE_KEY, REMINDERS_CACHE_KEY} from "api/cacheKeys";
import {activeConnection, shouldPrefetch} from "nav/preloadRoutes";

/**
 * Post-login DATA warm-up for the four private destinations (ui#284).
 *
 * {@link ../nav/preloadRoutes} already warms the route CHUNKS after login; this warms their LIST DATA
 * (contacts / groups / reminders / transfers) so the FIRST visit to a destination paints its rows
 * instantly instead of a cold-load skeleton — the residual "refresh feel" the owner reported. Each
 * list is a fire-and-forget background {@link prefetchResource}: it warms a cold or stale key only,
 * dedups with any in-flight load, and is discarded by the cache's generation guard if a logout races
 * it — so it can never repopulate the next account's cache.
 *
 * Guarded once by the browser's data-saver preference (same courtesy as the chunk warm-up). SSG-safe:
 * every feature Api is pulled in through a dynamic `import()` inside a function body, so importing this
 * module lands NO feature code in the eager bundle and touches no browser globals at module scope. The
 * loaders reuse the SAME specifiers the route chunks use, so the imports resolve to already-warmed
 * modules rather than emitting anything new.
 */
export function prefetchPrivateData(): void {
	if (!shouldPrefetch(activeConnection())) {
		return;
	}
	void import("contacts/contacts/ContactsApi").then(({ContactsApi}) =>
		prefetchResource(CONTACTS_CACHE_KEY, () => ContactsApi.getAll()));
	void import("contacts/groups/GroupApi").then(({default: GroupApi}) =>
		prefetchResource(GROUPS_CACHE_KEY, () => GroupApi.getAll()));
	void import("reminders/reminders/RemindersApi").then(({RemindersApi}) =>
		prefetchResource(REMINDERS_CACHE_KEY, () => RemindersApi.getAll()));
	// The list the screen opens on, under the key and in the shape useTransfers reads (one page,
	// kitecorp/transferify-api-service#2163).
	void import("transfers/transferListCache").then(({prefetchTransferList}) => prefetchTransferList());
}
